import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

interface SpeechServiceCallbacks {
  onRecognizing: (text: string) => void;
  onRecognized: (text: string) => void;
  onError: (error: string) => void;
  onStopped: () => void;
}

class SpeechService {
  private recognizer: SpeechSDK.SpeechRecognizer | null = null;
  private finalTranscript = '';

  private getSpeechConfig(): SpeechSDK.SpeechConfig {
    const key = process.env.REACT_APP_AZURE_SPEECH_KEY;
    const region = process.env.REACT_APP_AZURE_SPEECH_REGION;

    if (!key || !region || key === 'YOUR_AZURE_SPEECH_KEY') {
      throw new Error('Azure Speech Services not configured. Set REACT_APP_AZURE_SPEECH_KEY and REACT_APP_AZURE_SPEECH_REGION.');
    }

    const config = SpeechSDK.SpeechConfig.fromSubscription(key, region);
    config.speechRecognitionLanguage = 'en-GB';
    config.enableDictation();
    return config;
  }

  start(callbacks: SpeechServiceCallbacks): void {
    try {
      const speechConfig = this.getSpeechConfig();
      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      this.recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
      this.finalTranscript = '';

      this.recognizer.recognizing = (_sender, event) => {
        if (event.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
          callbacks.onRecognizing(this.finalTranscript + (this.finalTranscript ? ' ' : '') + event.result.text);
        }
      };

      this.recognizer.recognized = (_sender, event) => {
        if (event.result.reason === SpeechSDK.ResultReason.RecognizedSpeech && event.result.text) {
          this.finalTranscript += (this.finalTranscript ? ' ' : '') + event.result.text;
          callbacks.onRecognized(this.finalTranscript);
        }
      };

      this.recognizer.canceled = (_sender, event) => {
        if (event.reason === SpeechSDK.CancellationReason.Error) {
          callbacks.onError(event.errorDetails || 'Speech recognition error');
        }
        this.dispose();
        callbacks.onStopped();
      };

      this.recognizer.sessionStopped = () => {
        this.dispose();
        callbacks.onStopped();
      };

      this.recognizer.startContinuousRecognitionAsync(
        () => {},
        (err) => {
          callbacks.onError(`Failed to start: ${err}`);
          callbacks.onStopped();
        }
      );
    } catch (error: any) {
      callbacks.onError(error.message || 'Failed to initialize speech recognition');
      callbacks.onStopped();
    }
  }

  stop(): void {
    if (this.recognizer) {
      this.recognizer.stopContinuousRecognitionAsync(
        () => this.dispose(),
        () => this.dispose()
      );
    }
  }

  private dispose(): void {
    if (this.recognizer) {
      try { this.recognizer.close(); } catch {}
      this.recognizer = null;
    }
  }

  get isActive(): boolean {
    return this.recognizer !== null;
  }
}

const speechService = new SpeechService();
export default speechService;
